import classNames from 'classnames'
import { FC, PropsWithChildren } from 'react'

export interface Props extends PropsWithChildren {
	tag?: keyof JSX.IntrinsicElements
	size?: string
	color?: string | null
	font?: string | null
	className?: string
	id?: string
}

/**
 * Text is a functional component that renders text with customizable HTML tag, size, color, font, and additional classes.
 *
 * @param {Object} props - The component props.
 * @param {keyof JSX.IntrinsicElements} [props.tag='span'] - The HTML tag to use for the text element.
 * @param {string} [props.size='base'] - The size of the text.
 * @param {string | null} [props.color='black'] - The color of the text.
 * @param {string | null} [props.font='normal'] - The font weight/style of the text.
 * @param {string} [props.className] - Additional class names to apply to the text element.
 * @param {React.ReactNode} props.children - The content to be displayed inside the text element.
 * @returns {JSX.Element} A JSX element representing the text component.
 */
const Text: FC<Props> = ({
	tag = 'span',
	color = 'black',
	size = 'base',
	font = 'normal',
	children,
	className,
	id,
}) => {
	const Tag = tag

	return (
		<Tag
			id={id ?? ''}
			className={classNames(
				`text-${size} `,
				{ [`text-${color}`]: color },
				{ [`font-${font}`]: font },
				className
			)}
		>
			{children}
		</Tag>
	)
}

export default Text
